import React, { type ReactNode, useMemo } from "react";

import clsx from "clsx";
import moment from "moment";

import { useUser } from "@components/user-context";
import { type DataTypeProvider } from "@devexpress/dx-react-grid";
import { type User } from "@internal/user";
import { formatting } from "@north-beam/nb-common";

import { ACTUAL_WEEKLY_SPEND_ID, BUDGETED_WEEKLY_SPEND_ID } from "../constants";
import {
  type WeekData,
  WeekStatus,
  type TableRowType,
} from "../performance-tracking-table";

export const WeeklyPeriodFixedRowDataFormatter = ({
  column,
  row: _row,
}: DataTypeProvider.ValueFormatterProps) => {
  const { user } = useUser();
  const row = _row as TableRowType;

  const { weeklyMetrics } = row.data;
  const currentWeekNumber = useMemo(
    () =>
      weeklyMetrics.findIndex(({ status }) => status === WeekStatus.CURRENT),
    [weeklyMetrics],
  );
  const hasCurrentWeek = currentWeekNumber !== -1;
  const currentWeek = weeklyMetrics[currentWeekNumber];

  const field =
    column.name === BUDGETED_WEEKLY_SPEND_ID ? "budgeted" : "actual";

  return (
    <div
      dir="rtl"
      className={clsx(
        "flex items-center h-full text-right overflow-hidden relative",
        {
          "text-cosmic+1": column.name === ACTUAL_WEEKLY_SPEND_ID,
          "text-interstellar+1": column.name !== ACTUAL_WEEKLY_SPEND_ID,
        },
      )}
    >
      {hasCurrentWeek && (
        <div className="flex items-center justify-end h-full bg-v2-secondary z-1 w-28 absolute left-0">
          <div className="w-1 border-l border-cosmic-1 h-full" />
          <div className="pr-2 w-28">
            <WeekSummaryContent
              field={field}
              weekNumber={currentWeekNumber + 1}
              week={currentWeek}
              user={user}
            />
          </div>
        </div>
      )}
      <div dir="ltr" className="flex items-center">
        {weeklyMetrics.map((week, index) => {
          const isLastWeek = index === weeklyMetrics.length - 1;
          return (
            <div key={index} className="flex items-end justify-end ml-2 w-24">
              <div
                className={clsx(
                  "font-medium text-sm font-mono mx-1 w-24 pr-2",
                  {
                    "text-cosmic": column.name === ACTUAL_WEEKLY_SPEND_ID,
                    "text-interstellar-1":
                      column.name !== ACTUAL_WEEKLY_SPEND_ID,
                  },
                )}
              >
                <WeekSummaryContent
                  field={field}
                  weekNumber={index + 1}
                  week={week}
                  user={user}
                />
              </div>
              {!isLastWeek && (
                <div className="text-gray text-md font-medium">|</div>
              )}
            </div>
          );
        })}
      </div>
      <div className="w-28" />
    </div>
  );
};

const WeekSummaryContent = ({
  weekNumber,
  week,
  user,
  field,
}: {
  weekNumber: number;
  week: WeekData;
  user: User;
  field: "budgeted" | "actual";
}) => {
  const statusDisplay: Record<WeekStatus, ReactNode> = {
    [WeekStatus.COMPLETE]: (
      <div className="text-gray+1" dir="rtl">
        Complete
        <i className="fa-regular fa-check mr-0.5" />
      </div>
    ),
    [WeekStatus.CURRENT]: <div className="text-alien">In progress</div>,
    [WeekStatus.UPCOMING]: <div className="text-gray">Upcoming</div>,
  };

  const start = moment(week.dateRange.startDate);
  const startFormatted = start.isValid() ? start.format("MMM DD") : "Start";
  const end = moment(week.dateRange.endDate);
  const endFormatted = end.isValid() ? end.format("MMM DD") : "End";
  const dateRange = `${startFormatted} - ${endFormatted}`;

  return (
    <div className="font-sans flex flex-col space-y-[1px]">
      <div
        className={clsx(
          "font-semibold text-xs",
          week.status === "Complete" && "line-through",
        )}
      >
        WK{weekNumber}
      </div>
      <div className="text-xxs font-light text-v2-primary tracking-tight">
        {dateRange}
      </div>
      <div className="text-xxs font-light">{statusDisplay[week.status]}</div>
      <div className="font-medium font-mono text-sm">
        {formatting.formatCurrency(week[field], {
          precision: "approximate",
          code: user.currencies.currentCurrency,
        })}
      </div>
    </div>
  );
};
